import React, { useContext } from 'react';
import styled, { css } from 'styled-components';

import { SectionContainer } from './common/SectionContainer';
import Title from './common/Title';
import Subtitle from './common/Subtitle';
import Paragraph from './common/Paragraph';
import Button from './common/Button';

import { COLORS, SIZE } from './common/constants';
import { DarkModeContext } from './context/DarkModeContext';

const Testimonials = () => {
	const { isDarkModeOn } = useContext(DarkModeContext);
	return (
		<SectionContainer id='testimonials' isDarkModeOn={isDarkModeOn}>
			<TitleContainer>
				<Title title='Testimonials'></Title>
				<Button
					text='View on Linkedin'
					link='https://www.linkedin.com/in/dasom-won-8a1594190/'
				></Button>
			</TitleContainer>
			<Cards>
				<Card>
					<Subtitle subtitle='Dragos'></Subtitle>
					<Position>Software Engineer</Position>
					<ParagraphStyled>
						I've mentored Dasom during her time at ReDI where I was quickly
						impressed with{' '}
						<HighLight>
							her determination, eye for detail, and passion for technology
						</HighLight>
						. No matter the circumstances, whether it was a bug, an unclear
						feature, or a hard deadline, she kept persisting. She also has
						<HighLight> a highly organized approach</HighLight> when getting
						things done. Dasom documented her learning journey extremely well,
						always followed up on my hints with implementation, and made the
						most of our appointments. I am confident in her success as a
						software developer and she has earned my highest recommendation. All
						the best ahead :)
					</ParagraphStyled>
				</Card>
				<Card>
					<Subtitle subtitle='Cecilia'></Subtitle>
					<Position>Project Manager</Position>
					<ParagraphStyled>
						I was Dasom's manager during her time as a contractor for EF Hello.
						When Dasom started, we had begun to gradually scale our app and were
						about to launch in a new market. Working with Dasom is always{' '}
						<HighLight>a joy; </HighLight>
						she has produced{' '}
						<HighLight>
							high-quality work with close attention to detail.
						</HighLight>{' '}
						Dasom works with a good sense of
						<HighLight> urgency and engagement </HighLight>and I appreciate how
						she keeps in <HighLight>close communication </HighLight>
						updating me on the progress of her projects. Dasom always has a
						great attitude and I thoroughly enjoy working with her.
					</ParagraphStyled>
				</Card>
				<Card>
					<Subtitle subtitle='Sergio'></Subtitle>
					<Position>Software Engineer</Position>
					<ParagraphStyled>
						I was Dasom's React teacher. Dasom was no doubt on the top on her
						class. She could very easily be the best in class.{' '}
						<HighLight>
							She learned super fast, but was also extremely dedicated and
							attentive to details.
						</HighLight>{' '}
						This was a frontend programming course but Dasom was able to prove
						that her <HighLight>design skills</HighLight> are also amazing,
						nothing short of the professional designers that I work with
						everyday. This made her have{' '}
						<HighLight>one of the best final projects </HighLight>
						in her class. I know this is a recommendation, but I honestly can't
						think of something she wasn't good at. I would hire her myself if I
						could. If she doesn't have a job yet is because companies want the
						safe bet of someone who has worked in a production environment
						before. Hopefully this recommendation changes that.{' '}
						<HighLight>
							In terms of personality/cultural fit, she is a safe choice.
						</HighLight>{' '}
						Super easy going, funny and{' '}
						<HighLight>good at communication,</HighLight> I'm sure anyone who
						met her would quickly agree with me. If you are an employer, feel
						free to contact me for more info. She knows enough to be productive
						from DAY 1!
					</ParagraphStyled>
				</Card>
			</Cards>
		</SectionContainer>
	);
};

export default Testimonials;

// 🎨 Styled Components
const TitleContainer = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 2em;
	@media (max-width: ${SIZE.medium}) {
		flex-direction: column;
		align-items: flex-start;
	}
`;

const Cards = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	width: 100%;
	@media (max-width: ${SIZE.medium}) {
		display: flex;
		flex-direction: column;
	}
`;

const Card = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding: 1em 3em 2em 0;
	margin-bottom: 0.5em;
	@media (max-width: ${SIZE.medium}) {
		flex-direction: column;
		padding: 1em 0 2em 0;
	}
`;

const ParagraphStyled = styled.div`
	font-size: calc(14px + (16 - 14) * ((100vw - 300px) / (1600 - 300)));
	font-family: 'Montserrat', sans-serif;
	font-weight: 300;
	line-height: 1.8;
	text-align: left;
	color: ${COLORS.lightGrey};
	${(props) =>
		props.isDarkModeOn &&
		css`
			color: ${COLORS.white};
		`}
	&::selection {
		background-color: ${COLORS.somOrange};
		color: ${COLORS.white};
	}
`;

const HighLight = styled.span`
	font-size: calc(14px + (16 - 14) * ((100vw - 300px) / (1600 - 300)));
	font-family: 'Montserrat', sans-serif;
	font-weight: 300;
	line-height: 1.8;
	text-align: left;
	color: ${COLORS.somOrange}
		${(props) =>
			props.isDarkModeOn &&
			css`
				color: ${COLORS.white};
			`};
	&::selection {
		background-color: ${COLORS.somOrange};
		color: ${COLORS.white};
	}
`;

const Position = styled.h3`
	font-size: calc(15.6px + (16 - 14) * ((100vw - 300px) / (1600 - 300)));
	font-family: 'Montserrat', sans-serif;
	color: ${COLORS.grey};
	opacity: 0.6;
	font-weight: 500;
	text-align: left;
	margin-bottom: 1rem;
	${(props) =>
		props.isDarkModeOn &&
		css`
			color: ${COLORS.mainAqua};
		`}
	&::selection {
		background-color: ${COLORS.somOrange};
		color: ${COLORS.white};
	}
`;

const testimonialList = [
	{
		emoji: '👨‍💻',
		name: 'Dragos',
		position: 'Software Engineer',
		statement: `I've mentored Dasom during her time at ReDI where I was quickly impressed with her determination, eye for detail, and passion for technology. No matter the circumstances, whether it was a bug, an unclear feature, or a hard deadline, she kept persisting. She also has a highly organized approach when getting things done. Dasom documented her learning journey extremely well, always followed up on my hints with implementation, and made the most of our appointments. I am confident in her success as a software developer and she has earned my highest recommendation. All the best ahead :)`
	},
	{
		emoji: '👩‍💼',
		name: 'Cecilia',
		position: 'Product Manager',
		statement: `I was Dasom's manager during her time as a contractor for EF Hello. When Dasom started, we had begun to gradually scale our app and were about to launch in a new market. Working with Dasom is always a joy; she has produced high-quality work with close attention to detail. Dasom works with a good sense of urgency and engagement and I appreciate how she keeps in close communication updating me on the progress of her projects. Dasom always has a great attitude and I thoroughly enjoy working with her. `
	},
	{
		emoji: '👨‍💻',
		name: 'Sergio',
		position: 'Software Engineer',
		statement: `I was Dasom's React teacher.
		Dasom was no doubt on the top of her class. She could very easily be the best in class.
		She learned super fast, but was also extremely dedicated and attentive to details. This was a frontend programming course but Dasom was able to prove that her design skills are also amazing, nothing short of the professional designers that I work with everyday.
		This made her have one of, if not the best final project in her class.
		
		I know this is a recommendation, but I honestly can't think of something she wasn't good at. I would hire her myself if I could.
		If she doesn't have a job yet is because companies want the safe bet of someone who has worked in a production environment before.
		Hopefully this recommendation changes that.
		
		In terms of personality/cultural fit, she is a safe choice. Super easy going, funny and good at communication, I'm sure anyone who met her would quickly agree with me.
		
		If you are an employer, feel free to contact me for more info.
		
		She knows enough to be productive from DAY 1!`
	}
];
